import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, TrendingUp } from 'lucide-react';
import { testimonialsData } from '../../data/mock';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => 
        prev === testimonialsData.testimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => 
      prev === 0 ? testimonialsData.testimonials.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => 
      prev === testimonialsData.testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const currentTestimonial = testimonialsData.testimonials[currentIndex];

  return (
    <section 
      id="testimonials"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'white' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Star size={16} />
            <span className="font-semibold text-sm">Success Stories</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {testimonialsData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {testimonialsData.subtitle}
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className={`max-w-4xl mx-auto ${isVisible ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}>
          <div 
            className="card p-6 md:p-10 relative"
            style={{ background: 'var(--bg-page)' }}
          >
            {/* Quote Icon */}
            <div 
              className="absolute -top-4 left-8 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--brand-primary)' }}
            >
              <Quote size={24} style={{ color: 'var(--brand-dark)' }} />
            </div>

            {/* Testimonial Content */}
            <div className="pt-4">
              <p 
                className="body-large mb-8 leading-relaxed"
                style={{ color: 'var(--text-primary)' }}
              >
                "{currentTestimonial.quote}"
              </p>

              {/* Stats Card */}
              <div 
                className="inline-flex items-center gap-3 px-4 py-3 rounded-xl mb-8"
                style={{ background: 'var(--brand-dark)' }}
              >
                <TrendingUp size={20} style={{ color: 'var(--brand-primary)' }} />
                <div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {currentTestimonial.stats.metric}
                  </p>
                  <p className="text-sm font-semibold" style={{ color: 'white' }}>
                    {currentTestimonial.stats.before} → {currentTestimonial.stats.after}
                  </p>
                </div>
              </div>

              {/* Author */}
              <div className="flex items-center gap-4">
                <img 
                  src={currentTestimonial.image} 
                  alt={currentTestimonial.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {currentTestimonial.name}
                  </h4>
                  <p className="body-small" style={{ color: 'var(--text-light)' }}>
                    {currentTestimonial.role}, {currentTestimonial.company}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {currentTestimonial.location}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none px-2 md:-mx-16">
              <button
                onClick={goToPrev}
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 pointer-events-auto"
                style={{ background: 'white' }}
              >
                <ChevronLeft size={24} style={{ color: 'var(--brand-dark)' }} />
              </button>
              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 pointer-events-auto"
                style={{ background: 'white' }}
              >
                <ChevronRight size={24} style={{ color: 'var(--brand-dark)' }} />
              </button>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonialsData.testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => { setIsAutoPlaying(false); setCurrentIndex(index); }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'w-8' : 'w-2'
                }`}
                style={{ 
                  background: index === currentIndex ? 'var(--brand-dark)' : 'var(--border-light)'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
